import { LoadingGuard, Modal } from '@lifebit-ai/lds'
import React from 'react'

import FileContent from 'modules/resultsV2/components/FileContent/FileContent'

import * as styles from './FilePreviewModal.styles'
import type useViewFormModal from './useViewFormModal'
import { type FilePreview, type FormFile } from './useViewFormModal'

const { NoData, Loading, Error } = LoadingGuard

interface FilePreviewModalProps {
  open: boolean
  title: string
  resultId?: string
  selectedAttachmentPreview: FormFile | null
  selectedFileToPreview: FilePreview | null
  selectedSignedUrlPreview: FormFile | null
  verificationAttachmentSignedUrlQuery: ReturnType<typeof useViewFormModal>['verificationAttachmentSignedUrlQuery']
  onClose: () => void
}

type ModalBodyProps = Pick<
  FilePreviewModalProps,
  | 'resultId'
  | 'selectedAttachmentPreview'
  | 'selectedFileToPreview'
  | 'selectedSignedUrlPreview'
  | 'verificationAttachmentSignedUrlQuery'
>

const ModalBody = ({
  resultId,
  selectedAttachmentPreview,
  selectedFileToPreview,
  selectedSignedUrlPreview,
  verificationAttachmentSignedUrlQuery
}: ModalBodyProps) => {
  if (selectedAttachmentPreview) {
    // Use isFetching (not just isLoading) so we keep the spinner up while a stale
    // signed URL is being re-minted. Otherwise the query cache serves the previously
    // cached signed URL, FileContent renders the old file, then swaps to the new
    // one once the refetch resolves — causing a flicker and a duplicate download.
    if (verificationAttachmentSignedUrlQuery.isFetching) return <Loading />
    if (verificationAttachmentSignedUrlQuery.isError) {
      return <Error title="Unable to preview this attachment right now." />
    }

    if (!verificationAttachmentSignedUrlQuery.data?.signedUrl) {
      return <NoData title="Unable to preview this attachment right now." />
    }

    if (verificationAttachmentSignedUrlQuery.data.signedUrl) {
      return (
        <FileContent
          fileKey={verificationAttachmentSignedUrlQuery.data.fileKey || selectedAttachmentPreview.name}
          signedUrl={verificationAttachmentSignedUrlQuery.data.signedUrl}
        />
      )
    }
  }

  if (selectedFileToPreview) {
    if (!resultId) return null

    return (
      <FileContent
        file={selectedFileToPreview.file}
        resultId={resultId}
        bucket={selectedFileToPreview.bucket}
        fileKey={selectedFileToPreview.fileKey}
        containerName={selectedFileToPreview.containerName}
        provider={selectedFileToPreview.provider}
        storageAccountName={selectedFileToPreview.storageAccountName}
      />
    )
  }

  if (selectedSignedUrlPreview?.url) {
    return <FileContent fileKey={selectedSignedUrlPreview.name} signedUrl={selectedSignedUrlPreview.url} />
  }

  return null
}

const FilePreviewModal = ({
  open,
  title,
  resultId,
  selectedAttachmentPreview,
  selectedFileToPreview,
  selectedSignedUrlPreview,
  verificationAttachmentSignedUrlQuery,
  onClose
}: FilePreviewModalProps) => {
  return (
    <Modal onCancel={onClose} open={open} title={`View details: ${title}`} size="large" closable>
      <styles.ModalBodyWrapper>
        <ModalBody
          resultId={resultId}
          selectedAttachmentPreview={selectedAttachmentPreview}
          selectedFileToPreview={selectedFileToPreview}
          selectedSignedUrlPreview={selectedSignedUrlPreview}
          verificationAttachmentSignedUrlQuery={verificationAttachmentSignedUrlQuery}
        />
      </styles.ModalBodyWrapper>
    </Modal>
  )
}

export default FilePreviewModal
